import React, { useEffect, useState } from 'react'
import { IProps, IFeatureTabContentProps } from './index'
import { getColorTheme, fontGraphik, gridGap } from '../../utils'
import { FeatureGridItem } from '../../elements/FeatureGridItem'
import { containerMaxWidth } from 'marketing-site/src/library/utils'
import { Text } from '../../elements/Text'
import { Image } from '../../elements/Image'
import { FeatureTabV2 as FeatureTabV2Default } from './component'
import { BrandOptions, useBrand } from '../../providers/brandProvider'

export function FeatureTabV2(props: IProps) {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <FeatureTabV2Default {...props} />
  }
  return <FeatureTabV2Legacy {...props} />
}

export function FeatureTabV2Legacy({ bgColor, headline, featureTabContents }: IProps) {
  const themeColors = getColorTheme('legacy', bgColor)
  const [activeIndex, setActiveIndex] = useState(0)
  const [animation, setAnimation] = useState(true)
  const [activeTab, setActiveTab] = useState<IFeatureTabContentProps>()

  const setItem = (index: number) => {
    setActiveIndex(index)
    setActiveTab(featureTabContents[index])
    setAnimation(true)
  }

  useEffect(() => {
    if (featureTabContents.length > 0) {
      setActiveTab(featureTabContents[0])
    }
  }, [])

  return (
    <div className="feature-tab-container">
      <div
        className={`feature-tab ${animation ? 'active-animation' : ''}`}
        onAnimationEnd={() => setAnimation(false)}
      >
        <div className="left-content">
          <h2 className="headline">
            <Text size="xxl+">{headline}</Text>
          </h2>
          <div className="feature-tabs-wrapper">
            <ul>
              {featureTabContents.map((tab, index) => (
                <li key={index}>
                  <button
                    onClick={() => setItem(index)}
                    className={`tab-btn ${activeIndex === index ? 'active-btn' : ''}`}
                    type="button"
                    onAnimationEnd={() => setAnimation(false)}
                  >
                    {tab.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <ul className="items">
            {activeTab
              ? activeTab.items.map((item, index) => {
                  return (
                    <li key={index} className="list-item">
                      <FeatureGridItem {...item} />
                    </li>
                  )
                })
              : null}
          </ul>
        </div>
        <div className="right-content">
          {activeTab?.image ? (
            <div className="image">
              <Image {...activeTab.image} />
            </div>
          ) : null}
        </div>
      </div>
      <style jsx>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          .active-animation {
            animation: fadeIn 0.5s;
          }

          .active-btn {
            background-color: #ffffff;
          }

          .feature-tab-container {
            background-color: ${themeColors.backgroundColor};
            padding: 3rem 1.6rem;
          }

          .left-content {
            padding: 1.875rem 0;
            .headline {
              text-align: center;
            }
            .feature-tabs-wrapper {
              margin-top: 2rem;
            }
            .items {
              margin-top: 2rem;
              :global(.feature-grid-item) {
                text-align: center;
                margin-bottom: 2.25rem;
              }
            }
          }

          .right-content {
            text-align: center;
          }

          .feature-tabs-wrapper {
            z-index: 1;
            opacity: 1;
            text-align: center;
            & > ul {
              white-space: nowrap;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
              & > li {
                display: inline-block;
                margin: 0 10px;
                border-radius: 30px;
                overflow: hidden;
                @include media-query(mobile) {
                  margin: 0 5px;
                }
                &:first-of-type {
                  margin-left: 0;
                }
                &:last-of-type {
                  margin-right: 0;
                }
              }
            }

            .tab-btn {
              font-weight: bold;
              border-radius: 8px;
              padding: 15px;
              text-align: center;
              cursor: pointer;
              font-family: ${fontGraphik};
              &.active {
                background-color: #ffffff;
                color: ${themeColors.textColor};

                &:hover {
                  background-color: ${themeColors.backgroundColor};
                  color: ${themeColors.textColor};
                }
              }
              &:focus {
                outline: none;
              }
              &:hover {
                background-color: #ffffff;
                color: ${themeColors.textColor};
              }
            }
          }

          @media (${`min-width: 1140px`}) {
            .feature-tab-container {
              padding: 5rem 6.5rem;
            }
            .feature-tab {
              display: grid;
              grid-gap: 4.375rem;
              grid-template-columns: [left-content] 1fr [right-content] minmax(auto, 45%);
              max-width: ${containerMaxWidth};
              padding: 0;
              margin: 0 auto;
              position: relative;
              .left-content {
                padding: 1.875rem 0;
                .headline {
                  text-align: left;
                  .feature-tabs-wrapper {
                    margin-top: 2.75rem;
                  }
                }
                .feature-tabs-wrapper {
                  text-align: initial;
                  .items {
                    margin-top: 2rem;
                  }
                }
              }
              .right-content {
                max-width: 500px;
                margin: auto 0;
              }
            }
            .items {
              display: grid;
              grid-template-columns: 1fr 1fr;
              grid-column-gap: ${gridGap.desktop};
              .list-item {
                margin-bottom: 24px;
                :global(.feature-grid-item) {
                  text-align: left;
                  margin-bottom: 0;
                }
              }
            }
          }
        `}
      </style>
    </div>
  )
}
